<template>
  <div class="seller-products-page market-project market-project-v2">
    <div class="seller-products-page__layout">
      <div class="accFeed__wrapper matchmaking__wrapper">
        <div class="accFeed__sidebar matchmaking__sidebar">
          <div class="sidebar-block">
            <a class="project-details__back" href="#" @click.prevent="goBack">
              <img src="@/assets/images/icon-back.svg" alt="" class="project-details__back-icon" />
              Back to My Products
            </a>
          </div>
        </div>

        <div class="accFeed__content matchmaking__content">
          <div class="project-details">
            <div class="project-details__wrapper">
              <SuccessMessage v-if="successText" :text="successText" />

              <div class="project-details__header">
                <h1 class="project-details__title">
                  {{ productTitle }}
                  <MarketplaceUnreadBadge :count="productBulbCount" />
                </h1>
                <a
                  v-if="canEdit"
                  class="project-details__action edit"
                  href="#"
                  @click.prevent="openEditModal"
                >
                  <i class="icon-edit-project"></i>
                  Edit product
                </a>
              </div>

              <span class="project-details__deadline">
                Offer deadline: <strong>{{ offerDeadlineText }}</strong>.
                <br>
                Counter offers allowed: <strong>{{ offersAllowedText }}</strong>
              </span>

              <section class="make-offer-content">
                <div class="form-col-2">
                  <div class="form-col-1">
                    <label>Price, euro</label>
                    <span class="field">{{ priceText }}</span>
                  </div>
                  <div class="form-col-1">
                    <label>Delivery date</label>
                    <span class="field">{{ deliveryDateText }}</span>
                  </div>
                </div>

                <div class="form-col-2">
                  <div class="form-col-1">
                    <label>Company Name</label>
                    <span class="field">{{ safeText(product.company_name) }}</span>
                  </div>
                  <div class="form-col-1">
                    <label>Project Country</label>
                    <span class="field">{{ companyCountryText }}</span>
                  </div>
                </div>

                <div class="form-col-1 location mb-2">
                  <label>Project Location</label>
                  <span class="field">{{ safeText(product.company_address) }}</span>
                </div>

                <div
                  v-for="(pair, idx) in gridFieldsPairs"
                  :key="`pair-${idx}`"
                  class="form-col-2"
                >
                  <div v-if="pair[0]" class="form-col-1">
                    <label>{{ pair[0].name }}</label>
                    <span class="field">{{ formatValue(pair[0]) }}</span>
                  </div>
                  <div v-if="pair[1]" class="form-col-1">
                    <label>{{ pair[1].name }}</label>
                    <span class="field">{{ formatValue(pair[1]) }}</span>
                  </div>
                </div>

                <div class="form-col-1 location mb-2">
                  <label>Product details</label>
                  <span class="field">{{ safeText(product.detail) }}</span>
                </div>

                <div class="form-col-1 location mb-2">
                  <label>Product advantages/features</label>
                  <span class="field">{{ safeText(product.feature) }}</span>
                </div>

                <div
                  v-if="documents.length || mediaImages.length || videos.length"
                  class="addmedia"
                >
                  <div class="addmedia__downloaded">
                    <h5 class="addmedia__downloaded-title">Presentation and stuff</h5>

                    <div v-if="documents.length">
                      <h6>Documents ({{ documents.length }}):</h6>

                      <div
                        v-for="(doc, i) in documents"
                        :key="`doc-${i}`"
                        class="addmedia__downloaded-item"
                      >
                        <span class="icon-pdf"></span>
                        <a
                          class="addmedia__downloaded-name"
                          :href="doc.url"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {{ doc.title || doc.name || doc.url }}
                        </a>
                        <a
                          href="#"
                          @click.prevent="deleteDoc(doc)"
                          aria-label="Delete file"
                        >
                          <span class="icon-delete"></span>
                        </a>
                      </div>
                    </div>

                    <div v-if="mediaImages.length">
                      <h6>Images ({{ mediaImages.length }}):</h6>

                      <ul class="media-list">
                        <li
                          v-for="(img, i) in mediaImages"
                          :key="`img-${i}`"
                          class="media-item"
                        >
                          <a
                            href="#"
                            @click.prevent="deleteImage(img)"
                            aria-label="Delete image"
                          >
                            <span class="icon-delete"></span>
                          </a>
                          <img
                            :src="img.url"
                            :alt="img.title || img.name || 'image'"
                          >
                          <span>{{ img.title || img.name || img.url }}</span>
                        </li>
                      </ul>
                    </div>

                    <div v-if="videos.length">
                      <h6>Video ({{ videos.length }}):</h6>

                      <ul class="media-list">
                        <li
                          v-for="(v, i) in videos"
                          :key="`video-${i}`"
                          class="media-item"
                        >
                          <a
                            href="#"
                            @click.prevent="deleteVideo(v)"
                            aria-label="Delete video"
                          >
                            <span class="icon-delete"></span>
                          </a>
                          <iframe
                            :src="v.url"
                            :title="v.title || 'video'"
                            frameborder="0"
                            allow="
                              accelerometer;
                              autoplay;
                              clipboard-write;
                              encrypted-media;
                              gyroscope;
                              picture-in-picture;
                              web-share
                            "
                            allowfullscreen
                          ></iframe>
                          <span>{{ v.title || v.url }}</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div
                  v-if="statusKey !== 'draft' && (hasMessages || hasCounterOffers)"
                  class="market-project__actions flex-end"
                  data-panel-group
                >
                  <div class="market-project__buttons">
                    <a
                      v-if="hasMessages"
                      href="#"
                      class="btn-action"
                      data-toggle-panel="seller"
                      :data-panel-unread="productMessagesCount"
                      role="button"
                    >
                      {{ messagesCountText }}
                      <MarketplaceUnreadBadge :count="productMessagesCount" />
                    </a>
                    <a
                      v-if="hasCounterOffers"
                      href="#"
                      class="btn-action"
                      data-toggle-panel="offer"
                      role="button"
                    >
                      {{ counterOffersCountText }}
                      <MarketplaceUnreadBadge :count="counterOfferBulbCount" />
                    </a>
                  </div>

                  <ProductMessagesPanel
                    v-if="hasMessages"
                    ref="productMessagesPanel"
                    :productId="product.id"
                    :messages="liveProductMessages"
                  />

                  <ProductCounterOffersPanel
                    v-if="hasCounterOffers"
                    :productId="product.id"
                    :marketplaceId="marketplaceId"
                    role="seller"
                    :offers="product.offers || []"
                    @approve="onCounterOfferApproved"
                  />
                </div>
              </section>
            </div>
          </div>

          <div v-if="isLoading" class="d-flex justify-content-center p-2">
            <div class="loader"></div>
          </div>
        </div>
      </div>
    </div>

    <BModal
      id="modal-product-details-form"
      ref="productModal"
      size="xl"
      hide-header
      hide-footer
      no-close-on-backdrop
      @hidden="onProductModalHidden"
    >
      <ProductFormRequest
        :marketplaces="marketplaces"
        :marketplaceId="marketplaceSelectedId"
        :marketplaceName="marketplaceSelectedName"
        :countries="countries"
        :fields="productFields"
        :formId="formId"
        :isNewProduct="false"
        :product="product"
        @closeModal="closeProductModal"
        @onCreateProduct="onProductUpdated"
        @onUpdateProduct="onProductUpdated"
        @onDeleteProduct="onProductDeleted"
      />
    </BModal>
  </div>
</template>

<script>
import { BModal } from 'bootstrap-vue';
import MatchmakingService from '@/services/MatchmakingService';
import DictionaryService from '@/services/DictionaryService';
import ProductFormRequest from '@/components/matchmaking/ProductFormRequest.vue';
import ProductMessagesPanel from '@/components/matchmaking/ProductMessagesPanel.vue';
import ProductCounterOffersPanel from '@/components/matchmaking/ProductCounterOffersPanel.vue';
import MarketplaceUnreadBadge from '@/components/matchmaking/MarketplaceUnreadBadge.vue';
import SuccessMessage from '@/components/matchmaking/SuccessMessage.vue';
import marketplaceProductMessages from '@/mixins/marketplaceProductMessages';

export default {
  name: 'SellerProductDetailsPage',
  components: {
    BModal,
    ProductFormRequest,
    ProductMessagesPanel,
    ProductCounterOffersPanel,
    MarketplaceUnreadBadge,
    SuccessMessage,
  },
  mixins: [marketplaceProductMessages],
  data() {
    return {
      isLoading: false,
      product: {},
      marketplaces: [],
      marketplaceSelectedId: null,
      countries: [],
      productFields: [],
      formId: '',
      successText: '',
      successTimer: null,
    };
  },
  computed: {
    marketplaceSelectedName() {
      if (!this.marketplaceSelectedId) return 'All';
      const found = (this.marketplaces || [])
        .find((m) => String(m.id) === String(this.marketplaceSelectedId));
      return found ? found.name : 'All';
    },

    productTitle() {
      return this.product && this.product.name ? String(this.product.name) : '—';
    },

    marketplaceId() {
      const p = this.product || {};
      return p.marketplace_id ?? (p.marketplace && p.marketplace.id) ?? null;
    },

    productBulbCount() {
      return this.$store.getters.productBulb('seller', this.product && this.product.id);
    },

    mpMessageRole() {
      return 'seller';
    },
    mpMessageProductId() {
      return this.product && this.product.id;
    },
    mpBaseMessages() {
      return Array.isArray(this.product && this.product.messages) ? this.product.messages : [];
    },
    mpProductOffers() {
      return Array.isArray(this.product && this.product.offers) ? this.product.offers : [];
    },

    companyCountryText() {
      const cc = this.product && this.product.company_country;
      if (!cc) return '—';
      if (typeof cc === 'object') return cc.name || cc.iso || '—';
      return String(cc);
    },

    offerDeadlineText() {
      const raw = this.product && (this.product.deadline_at || this.product.deadlineAt);
      if (!raw) return 'not specified';
      return String(raw).trim() || 'not specified';
    },

    deliveryDateText() {
      const raw = this.product && (this.product.delivery_date || this.product.deliver_date);
      if (raw) return String(raw).trim() || 'not specified';
      return this.offerDeadlineText;
    },

    offersAllowedText() {
      const p = this.product || {};
      const v = (p.offers_allow ?? p.offersAllow ?? p.offer_allow ?? p.offers_allow);
      return Number(v) === 1 || v === true ? 'Yes' : 'No';
    },

    statusKey() {
      const p = this.product || {};
      const rawName = String(p.status_name || p.statusName || '').toLowerCase();
      const statusNum = Number(p.status);
      if (rawName.includes('draft') || statusNum === 3) return 'draft';
      return 'offered';
    },

    canEdit() {
      const p = this.product || {};
      const rawStatus = typeof p.status === 'string' ? p.status : '';
      const rawName = String(p.status_name || p.statusName || p?.status?.name || rawStatus || '')
        .toLowerCase();
      if (rawName.includes('approved') || rawName.includes('declined')) return false;

      const statusNum = Number(p?.status?.id ?? p.status);
      return statusNum !== 2 && statusNum !== 4;
    },

    normalizedFields() {
      const raw = this.productFields;
      const list = Array.isArray(raw) ? raw.slice() : [];
      return list.map((f) => {
        const field = { ...f };
        field.id = String(field.id);

        if (field.type === 'dropbox') {
          const vals = field.values;

          if (Array.isArray(vals)) {
            if (vals.length && typeof vals[0] !== 'object') {
              field.values = vals.map((v, idx) => ({ id: idx, value: v }));
            } else {
              field.values = vals.map((v, idx) => ({
                id: v && v.id !== undefined ? v.id : idx,
                value: v && (v.value || v.name) ? (v.value || v.name) : String(v),
              }));
            }
          } else if (vals && typeof vals === 'object') {
            field.values = Object.keys(vals).map((k) => ({ id: Number(k), value: vals[k] }));
          } else {
            field.values = [];
          }
        }

        return field;
      }).filter((f) => f && f.id);
    },

    gridFields() {
      return (this.normalizedFields || []).filter((f) => f && f.type !== 'textarea');
    },

    gridFieldsPairs() {
      const pairs = [];
      const list = this.gridFields;
      for (let i = 0; i < list.length; i += 2) {
        pairs.push([list[i], list[i + 1]]);
      }
      return pairs;
    },

    priceText() {
      const hasPrice = this.product && this.product.price !== undefined
        && this.product.price !== null;
      if (hasPrice && String(this.product.price).trim() !== '') return String(this.product.price);
      return '—';
    },

    documents() {
      const docs = Array.isArray(this.product.documents) ? this.product.documents : [];
      return docs.filter((d) => d && d.url && !String(d.url).toLowerCase().match(/\.(png|jpg|jpeg|webp|gif)$/));
    },

    mediaImages() {
      const docs = Array.isArray(this.product.documents) ? this.product.documents : [];
      return docs.filter((d) => d && d.url && String(d.url).toLowerCase().match(/\.(png|jpg|jpeg|webp|gif)$/));
    },

    videos() {
      return Array.isArray(this.product.video) ? this.product.video : [];
    },

    messagesCount() {
      const p = this.product || {};
      const fromCount = Number(p.messages_count ?? p.message_threads_count ?? 0);
      const fromArray = Array.isArray(p.messages) ? p.messages.length : 0;
      const fromLive = Array.isArray(this.liveProductMessages) ? this.liveProductMessages.length : 0;
      return Math.max(fromCount, fromArray, fromLive);
    },
    messagesCountText() {
      return `${this.messagesCount} message${this.messagesCount === 1 ? '' : 's'} from Buyers`;
    },

    counterOffersCount() {
      const p = this.product || {};
      const fromCount = Number(p.counter_offers_count ?? p.offers_count ?? 0);
      const fromArray = Array.isArray(p.offers) ? p.offers.length : 0;
      return fromCount > 0 ? fromCount : fromArray;
    },
    counterOffersCountText() {
      return `${this.counterOffersCount} Counter Offer${this.counterOffersCount === 1 ? '' : 's'}`;
    },

    // Show Messages/Counter Offer buttons only when API indicates data exists (like product list)
    hasMessages() {
      const p = this.product || {};
      const hasCount = Number(p.messages_count ?? p.message_threads_count ?? 0) > 0;
      const hasArray = Array.isArray(p.messages) && p.messages.length > 0;
      const hasLive = Array.isArray(this.liveProductMessages) && this.liveProductMessages.length > 0;
      return Boolean(hasCount || hasArray || hasLive || this.productMessagesCount > 0);
    },
    hasCounterOffers() {
      const p = this.product || {};
      const hasCount = Number(p.counter_offers_count ?? p.offers_count ?? 0) > 0;
      const hasArray = Array.isArray(p.offers) && p.offers.length > 0;
      return Boolean(hasCount || hasArray);
    },
  },
  watch: {
    '$route.params.id': {
      immediate: false,
      handler() {
        this.fetchProduct();
      },
    },
  },
  async created() {
    await this.fetchMarketplaces();
    await this.fetchCountries();
    await this.fetchProduct();
  },

  beforeDestroy() {
    if (this.successTimer) clearTimeout(this.successTimer);
  },

  methods: {
    showSuccess(text) {
      this.successText = text;
      if (this.successTimer) clearTimeout(this.successTimer);
      this.successTimer = setTimeout(() => {
        this.successText = '';
        this.successTimer = null;
      }, 4000);
    },

    safeText(v) {
      const s = String(v || '').trim();
      return s || '—';
    },

    async fetchMarketplaces() {
      try {
        const { data } = await MatchmakingService.marketplaces();
        this.marketplaces = Array.isArray(data) ? data : [];
      } catch (e) {
        this.marketplaces = [];
      }
    },

    async fetchCountries() {
      try {
        if (Array.isArray(this.marketplaces) && this.marketplaces.length) {
          const withCountries = this.marketplaces.find(
            (m) => m && m.filters && Array.isArray(m.filters.countries)
              && m.filters.countries.length,
          );
          if (withCountries) {
            this.countries = withCountries.filters.countries;
            return;
          }
        }

        const { data } = await DictionaryService.countries();
        this.countries = Array.isArray(data) ? data : [];
      } catch (e) {
        this.countries = [];
      }
    },

    async fetchProduct() {
      const id = this.$route.params && this.$route.params.id;
      if (!id) return;

      this.isLoading = true;
      try {
        const { data } = await MatchmakingService.marketplaceProduct(id);
        this.product = data || {};

        const mpId = this.product && (this.product.marketplace_id
          || (this.product.marketplace && this.product.marketplace.id));
        if (mpId) this.marketplaceSelectedId = mpId;

        this.productFields = Array.isArray(this.product.data)
          ? this.product.data.map((f) => ({ ...f }))
          : [];
      } catch (e) {
        this.product = {};
        this.productFields = [];
      } finally {
        this.isLoading = false;

        this.$nextTick(() => {
          const panel = String((this.$route.query && this.$route.query.panel) || '');
          if (!panel) return;
          const btn = this.$el && this.$el.querySelector(`[data-toggle-panel="${panel}"]`);
          if (btn) btn.click();
        });
      }
    },

    goBack() {
      this.$router.push({ name: 'profileMatchmakingSellerProducts' }).catch(() => {});
    },

    openEditModal() {
      if (!this.canEdit || !this.product || !this.product.id) return;
      this.formId = String(this.product.id);
      // eslint-disable-next-line no-unused-expressions
      this.$refs.productModal && this.$refs.productModal.show();
    },
    closeProductModal() {
      // eslint-disable-next-line no-unused-expressions
      this.$refs.productModal && this.$refs.productModal.hide();
    },
    onProductModalHidden() {
      this.formId = '';
    },
    onProductUpdated() {
      this.showSuccess('Product was updated');
      this.closeProductModal();
      this.fetchProduct();
    },
    onProductDeleted() {
      this.closeProductModal();
      this.goBack();
    },

    async onCounterOfferApproved({ offer }) {
      const offerId = offer?.id ?? offer?.offer_id;
      if (!offerId) return;
      try {
        await MatchmakingService.changeProductOfferStatus({
          offer_id: String(offerId),
          value: 3,
        });
        this.showSuccess('Counter offer approved');
        await this.fetchProduct();
      } catch (e) {
        this.showSuccess('Failed to approve');
        // eslint-disable-next-line no-console
        console.error('Approve counter-offer failed:', e);
      }
    },

    getFieldRaw(field) {
      const p = this.product || {};
      const { data } = p;
      if (!field || !field.id || !data) return null;

      const id = String(field.id);

      // array form: [{id:"field_2", initValue:"222"}, ...]
      if (Array.isArray(data)) {
        const found = data.find((it) => it && String(it.id) === id);
        if (!found) return null;
        return found.initValue ?? found.value ?? found.selected ?? null;
      }

      if (typeof data === 'object') {
        return Object.prototype.hasOwnProperty.call(data, id) ? data[id] : null;
      }

      return null;
    },

    formatValue(field) {
      const raw = this.getFieldRaw(field);
      if (raw === null || raw === undefined || raw === '') return '—';

      if (field.type !== 'dropbox') return String(raw);

      const list = Array.isArray(field.values) ? field.values : [];
      const found = list.find((v) => String(v.id) === String(raw));
      if (found) return String(found.value || found.name || raw);
      return String(raw);
    },

    async deleteDoc(doc) {
      if (!this.product || !this.product.id || !doc || !doc.url) return;
      try {
        await MatchmakingService.deleteMarketplaceProductMedia(this.product.id, {
          type: 'doc',
          name: doc.title || doc.name || doc.url,
        });
        this.fetchProduct();
      } catch (e) {
        // ignore
      }
    },
    async deleteImage(img) {
      if (!this.product || !this.product.id || !img || !img.url) return;
      try {
        await MatchmakingService.deleteMarketplaceProductMedia(this.product.id, {
          type: 'image',
          name: img.title || img.name || img.url,
        });
        this.fetchProduct();
      } catch (e) {
        // ignore
      }
    },
    async deleteVideo(v) {
      if (!this.product || !this.product.id || !v || !v.url) return;
      try {
        await MatchmakingService.deleteMarketplaceProductMedia(this.product.id, {
          type: 'link',
          name: v.title || v.url,
        });
        this.fetchProduct();
      } catch (e) {
        // ignore
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.project-details__back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
  color: #1d9dd9;
  font-weight: 600;
  font-size: 14px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &-icon {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
  }
}
</style>
